import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
const tok = () => localStorage.getItem('token');
const gh = (n) => `GHS ${Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2 })}`;
const hdrs = () => ({ Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json' });

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', sky: '#38BDF8', lavender: '#A78BFA',
  text: '#E8EDF5', sub: '#C5CDD8',
};

const TAB_KEYS   = ['expiry', 'reorder', 'suppliers', 'batches'];
const TAB_LABELS = ['Expiry Tracking', 'Auto-Reorder', 'Suppliers', 'Batch Tracking'];

const STYLES = `
  .ii-shell { padding: 28px 32px; max-width: 1200px; margin: 0 auto; font-family: 'Cabinet Grotesk', sans-serif; }
  .ii-title { font-size: 1.5rem; font-weight: 800; color: ${C.text}; margin: 0; }
  .ii-subtitle { font-size: 0.875rem; color: ${C.muted}; margin: 6px 0 28px; }
  .ii-tabs {
    display: flex; gap: 2px; background: rgba(255,255,255,0.04); border-radius: 12px;
    padding: 4px; margin-bottom: 24px; width: fit-content; flex-wrap: wrap;
  }
  .ii-tab {
    padding: 9px 20px; border-radius: 10px; font-size: 0.82rem; font-weight: 600;
    cursor: pointer; border: none; transition: all .15s; font-family: inherit;
    background: transparent; color: ${C.muted};
  }
  .ii-tab.active { background: ${C.surface}; color: ${C.text}; font-weight: 700; box-shadow: 0 1px 6px rgba(0,0,0,0.3); }
  .ii-content { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 16px; padding: 24px; }

  /* KPI */
  .ii-kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 12px; margin-bottom: 20px; }
  .ii-kpi {
    background: rgba(255,255,255,0.03); border: 1px solid ${C.border}; border-radius: 12px;
    padding: 16px 20px; display: flex; align-items: center; gap: 14px;
  }
  .ii-kpi-icon { width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
  .ii-kpi-val { font-size: 1.5rem; font-weight: 800; line-height: 1; font-family: 'JetBrains Mono', monospace; }
  .ii-kpi-label { font-size: 0.75rem; color: ${C.muted}; margin-top: 4px; }

  /* Alerts */
  .ii-alert { padding: 12px 16px; border-radius: 10px; margin-bottom: 12px; font-size: 0.875rem; font-weight: 600; }
  .ii-alert.danger { background: rgba(255,107,107,0.1); border: 1.5px solid rgba(255,107,107,0.3); color: ${C.coral}; }
  .ii-alert.warn { background: rgba(240,180,41,0.1); border: 1.5px solid rgba(240,180,41,0.3); color: ${C.gold}; }
  .ii-alert.success { background: rgba(0,201,177,0.1); border: 1.5px solid rgba(0,201,177,0.25); color: ${C.teal}; }

  /* Filter chips */
  .ii-filter-row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 16px; }
  .ii-chip {
    padding: 6px 14px; border-radius: 20px; font-size: 0.75rem; font-weight: 600;
    cursor: pointer; border: none; transition: all .15s; font-family: inherit;
  }
  .ii-chip.inactive { background: rgba(255,255,255,0.06); color: ${C.muted}; }
  .ii-chip.active { background: rgba(240,180,41,0.15); color: ${C.gold}; border: 1px solid rgba(240,180,41,0.3); }
  .ii-chip-outline {
    padding: 5px 12px; border-radius: 8px; font-size: 0.75rem; font-weight: 600;
    cursor: pointer; transition: all .15s; font-family: inherit; background: transparent;
    border: 1px solid ${C.border}; color: ${C.muted};
  }
  .ii-chip-outline.active { border-color: ${C.gold}; color: ${C.gold}; background: rgba(240,180,41,0.08); }

  /* Table */
  .ii-table-wrap { overflow-x: auto; }
  .ii-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
  .ii-table th {
    padding: 10px 14px; text-align: left; font-size: 0.72rem; font-weight: 700;
    color: ${C.muted}; background: rgba(255,255,255,0.03);
    border-bottom: 1.5px solid ${C.border}; white-space: nowrap; text-transform: uppercase; letter-spacing: .05em;
  }
  .ii-table td { padding: 10px 14px; border-bottom: 1px solid rgba(42,46,69,0.5); color: ${C.sub}; }
  .ii-table tbody tr:last-child td { border-bottom: none; }
  .ii-table tbody tr:hover { background: rgba(255,255,255,0.025); }
  .ii-cell-name { font-weight: 600; color: ${C.text}; }
  .ii-badge {
    font-size: 0.68rem; font-weight: 700; padding: 3px 10px; border-radius: 20px;
    white-space: nowrap; display: inline-block; border: 1px solid;
  }
  .ii-mono { font-family: 'JetBrains Mono', monospace; font-weight: 700; }
  .ii-loading { text-align: center; padding: 40px; color: ${C.muted}; }

  /* Buttons */
  .ii-btn-primary {
    padding: 9px 20px; background: ${C.gold}; color: #0B0E17; border: none;
    border-radius: 8px; font-weight: 700; font-size: 0.875rem; cursor: pointer; transition: opacity .15s; font-family: inherit;
  }
  .ii-btn-primary:hover { opacity: .88; }
  .ii-btn-primary:disabled { opacity: .5; cursor: not-allowed; }
  .ii-btn-outline {
    padding: 7px 14px; background: transparent; border: 1px solid ${C.border};
    border-radius: 7px; color: ${C.sub}; font-size: 0.78rem; font-weight: 600;
    cursor: pointer; transition: all .15s; font-family: inherit;
  }
  .ii-btn-outline:hover { border-color: ${C.gold}; color: ${C.gold}; }
  .ii-btn-outline:disabled { opacity: .5; cursor: not-allowed; }
  .ii-btn-danger {
    padding: 5px 12px; background: rgba(255,107,107,0.1); color: ${C.coral};
    border: 1px solid rgba(255,107,107,0.3); border-radius: 6px; font-size: 0.75rem;
    font-weight: 600; cursor: pointer; transition: all .15s; font-family: inherit;
  }
  .ii-btn-danger:hover { background: rgba(255,107,107,0.18); }
  .ii-btn-danger:disabled { opacity: .5; cursor: not-allowed; }
  .ii-btn-ghost {
    padding: 5px 12px; background: rgba(255,255,255,0.05); color: ${C.muted};
    border: none; border-radius: 6px; font-size: 0.75rem; font-weight: 600; cursor: pointer; font-family: inherit;
  }
  .ii-btn-ghost:hover { background: rgba(255,255,255,0.09); }
  .ii-btn-edit {
    padding: 5px 12px; background: rgba(167,139,250,0.1); color: ${C.lavender};
    border: 1px solid rgba(167,139,250,0.25); border-radius: 6px; font-size: 0.75rem;
    font-weight: 600; cursor: pointer; font-family: inherit; transition: all .15s;
  }
  .ii-btn-edit:hover { background: rgba(167,139,250,0.18); }
  .ii-btn-order {
    padding: 6px 14px; background: ${C.teal}; color: #0B0E17; border: none;
    border-radius: 7px; font-size: 0.75rem; font-weight: 700; cursor: pointer; transition: opacity .15s; font-family: inherit;
  }
  .ii-btn-order:hover { opacity: .85; }
  .ii-btn-order:disabled { opacity: .5; cursor: not-allowed; }
  .ii-btn-order-all {
    margin-left: auto; padding: 8px 18px; background: ${C.coral}; color: white;
    border: none; border-radius: 8px; font-weight: 700; font-size: 0.82rem; cursor: pointer; font-family: inherit;
  }
  .ii-btn-warn {
    padding: 5px 12px; background: rgba(240,180,41,0.1); color: ${C.gold};
    border: 1px solid rgba(240,180,41,0.3); border-radius: 6px; font-size: 0.75rem; font-weight: 600; cursor: pointer; font-family: inherit;
  }

  /* Supplier avatar */
  .ii-sup-avatar {
    width: 36px; height: 36px; border-radius: 9px; font-weight: 700; font-size: 0.82rem;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    background: rgba(0,201,177,0.12); color: ${C.teal}; border: 1px solid rgba(0,201,177,0.2);
  }

  /* Stock bar */
  .ii-stock-bar-wrap { display: flex; align-items: center; gap: 8px; min-width: 110px; }
  .ii-stock-bar-track { flex: 1; height: 6px; border-radius: 3px; background: rgba(255,255,255,0.08); overflow: hidden; }
  .ii-stock-bar-fill { height: 100%; border-radius: 3px; background: ${C.coral}; transition: width .5s; }

  /* Row between */
  .ii-row-between { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; flex-wrap: wrap; gap: 10px; }
  .ii-row-meta { font-size: 0.875rem; color: ${C.muted}; }

  /* Stars */
  .ii-star-filled { color: ${C.gold}; }
  .ii-star-empty { color: rgba(255,255,255,0.15); }

  /* Source badge */
  .ii-source-batch { background: rgba(167,139,250,0.12); color: ${C.lavender}; border-color: rgba(167,139,250,0.25); }
  .ii-source-item  { background: rgba(0,201,177,0.12);   color: ${C.teal};     border-color: rgba(0,201,177,0.25);   }

  /* Batch filter select */
  .ii-filter-select {
    padding: 7px 12px; background: rgba(255,255,255,0.04); border: 1px solid ${C.border};
    border-radius: 8px; color: ${C.text}; font-size: 0.78rem; outline: none; font-family: inherit;
    transition: border-color .15s; width: 180px;
  }
  .ii-filter-select:focus { border-color: ${C.teal}; }
  select.ii-filter-select option { background: ${C.surface}; }

  /* Empty state */
  .ii-empty {
    text-align: center; padding: 48px 24px; border-radius: 12px;
    border: 1.5px dashed ${C.border}; background: rgba(255,255,255,0.015);
  }
  .ii-empty-icon { font-size: 2.25rem; margin-bottom: 12px; }
  .ii-empty-title { font-weight: 700; font-size: 0.95rem; color: ${C.text}; margin-bottom: 8px; }
  .ii-empty-sub { font-size: 0.82rem; color: ${C.muted}; max-width: 340px; margin: 0 auto; line-height: 1.6; }

  /* Modal */
  .ii-overlay {
    position: fixed; inset: 0; background: rgba(7,9,14,0.75);
    backdrop-filter: blur(4px); z-index: 1000;
    display: flex; align-items: center; justify-content: center; padding: 16px;
  }
  .ii-modal {
    background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 16px;
    width: 100%; max-height: 90vh; overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0,0,0,0.5); animation: ii-enter .18s ease;
  }
  @keyframes ii-enter { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
  .ii-modal-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 20px 24px; border-bottom: 1px solid ${C.border};
    position: sticky; top: 0; background: ${C.surface}; z-index: 1;
  }
  .ii-modal-title { font-size: 1.1rem; font-weight: 700; color: ${C.text}; margin: 0; }
  .ii-modal-close {
    background: none; border: none; font-size: 1.4rem; cursor: pointer;
    color: ${C.muted}; line-height: 1; transition: color .15s;
  }
  .ii-modal-close:hover { color: ${C.coral}; }
  .ii-modal-body { padding: 24px; display: flex; flex-direction: column; gap: 14px; }
  .ii-modal-foot { display: flex; gap: 10px; justify-content: flex-end; }

  .ii-field { display: flex; flex-direction: column; gap: 6px; }
  .ii-field-label { font-size: 0.82rem; font-weight: 600; color: ${C.sub}; }
  .ii-field-hint { font-size: 0.68rem; color: ${C.muted}; margin-top: 2px; }
  .ii-input {
    width: 100%; padding: 10px 12px; border: 1.5px solid ${C.border}; border-radius: 8px;
    font-size: 0.875rem; font-family: inherit; box-sizing: border-box; outline: none;
    background: rgba(255,255,255,0.04); color: ${C.text}; transition: border-color .15s;
  }
  .ii-input:focus { border-color: ${C.teal}; }
  select.ii-input option { background: ${C.surface}; }
  .ii-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  @media (max-width: 460px) { .ii-2col { grid-template-columns: 1fr; } }

  .ii-rating-row { display: flex; gap: 4px; }
  .ii-star-btn { font-size: 1.6rem; background: none; border: none; cursor: pointer; padding: 0 2px; transition: transform .1s; }
  .ii-star-btn:hover { transform: scale(1.15); }

  .ii-confirm-text { font-size: 0.875rem; color: ${C.sub}; line-height: 1.7; margin-bottom: 20px; }
  .ii-checkbox-row { display: flex; align-items: center; gap: 6px; font-size: 0.82rem; cursor: pointer; color: ${C.sub}; }
`;

// ── Shared sub-components ──────────────────────────────────────────────────────

function Modal({ title, onClose, children, maxWidth = 520 }) {
  return (
    <div className="ii-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ii-modal" style={{ maxWidth }}>
        <div className="ii-modal-head">
          <h3 className="ii-modal-title">{title}</h3>
          <button className="ii-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="ii-modal-body">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="ii-field">
      <label className="ii-field-label">{label}</label>
      {children}
      {hint && <div className="ii-field-hint">{hint}</div>}
    </div>
  );
}

function KPI({ label, value, color, icon }) {
  return (
    <div className="ii-kpi">
      <div className="ii-kpi-icon" style={{ background: `${color}18` }}>{icon}</div>
      <div>
        <div className="ii-kpi-val" style={{ color }}>{value}</div>
        <div className="ii-kpi-label">{label}</div>
      </div>
    </div>
  );
}

function ToastMsg({ msg }) {
  if (!msg) return null;
  return <div className="ii-alert success" style={{ marginBottom: 16 }}>✓ {msg}</div>;
}

function EmptyState({ icon, title, sub }) {
  return (
    <div className="ii-empty">
      <div className="ii-empty-icon">{icon}</div>
      <div className="ii-empty-title">{title}</div>
      <div className="ii-empty-sub">{sub}</div>
    </div>
  );
}

function daysLabel(d) {
  if (d < 0)  return { text: `${Math.abs(d)}d ago`, color: C.coral };
  if (d < 8)  return { text: `${d} days`,           color: C.coral };
  if (d < 31) return { text: `${d} days`,           color: C.gold  };
  if (d < 91) return { text: `${d} days`,           color: '#CA8A04' };
  return        { text: `${d} days`,                color: C.teal  };
}

const EXPIRY_STATUS_MAP = {
  expired:  { label: 'Expired',  bg: 'rgba(255,107,107,0.12)', color: C.coral,   border: 'rgba(255,107,107,0.3)' },
  critical: { label: 'Critical', bg: 'rgba(255,107,107,0.1)',  color: '#FF8C6B', border: 'rgba(255,107,107,0.25)' },
  warning:  { label: 'Warning',  bg: 'rgba(240,180,41,0.12)',  color: C.gold,    border: 'rgba(240,180,41,0.3)' },
  watch:    { label: 'Watch',    bg: 'rgba(240,180,41,0.08)',  color: '#CA8A04', border: 'rgba(240,180,41,0.2)' },
  good:     { label: 'Good',     bg: 'rgba(0,201,177,0.12)',   color: C.teal,    border: 'rgba(0,201,177,0.25)' },
};

// ── Tab 1: Expiry Tracking ─────────────────────────────────────────────────────

function ExpiryTab() {
  const [data, setData]     = useState(null);
  const [filter, setFilter] = useState('all');
  const [days, setDays]     = useState(90);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/inventory-alerts/expiring?days=${days}`, { headers: hdrs() });
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const all      = data?.all      || [];
  const expired  = data?.expired  || [];
  const critical = data?.critical || [];

  const filtered = (() => {
    if (filter === 'all')     return all;
    if (filter === 'expired') return expired;
    if (filter === '<7d')     return critical;
    if (filter === '<30d')    return [...critical, ...(data?.warning || [])];
    if (filter === '<90d')    return [...critical, ...(data?.warning || []), ...(data?.watch || [])];
    return all;
  })();

  return (
    <div>
      <div className="ii-kpi-grid">
        <KPI label="Expired"       value={expired.length}                                                                            color={C.coral} icon="⛔" />
        <KPI label="Expiring <30d" value={critical.length + (data?.warning?.length || 0)}                                           color={C.gold}  icon="🟠" />
        <KPI label="Expiring <90d" value={critical.length + (data?.warning?.length || 0) + (data?.watch?.length || 0)}              color="#CA8A04" icon="🟡" />
        <KPI label="Safe Stock"    value={data?.good?.length || 0}                                                                   color={C.teal}  icon="✅" />
      </div>

      {expired.length > 0 && (
        <div className="ii-alert danger">
          🚨 {expired.length} item{expired.length > 1 ? 's have' : ' has'} EXPIRED — remove from shelf:&nbsp;
          {expired.slice(0, 3).map((r) => r.name).join(', ')}{expired.length > 3 ? ` +${expired.length - 3} more` : ''}
        </div>
      )}
      {critical.length > 0 && (
        <div className="ii-alert warn" style={{ marginBottom: 16 }}>
          ⚠️ {critical.length} item{critical.length > 1 ? 's are' : ' is'} expiring within 7 days
        </div>
      )}

      <div className="ii-filter-row">
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {[['all','All'],['expired','Expired'],['<7d','<7 days'],['<30d','<30 days'],['<90d','<90 days']].map(([k, l]) => (
            <button key={k} className={`ii-chip ${filter === k ? 'active' : 'inactive'}`} onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <span style={{ fontSize: '0.82rem', color: C.muted }}>Window:</span>
          {[30, 60, 90].map((d) => (
            <button key={d} className={`ii-chip-outline${days === d ? ' active' : ''}`} onClick={() => setDays(d)}>{d}d</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="ii-loading">Loading…</div>
      ) : all.length === 0 ? (
        <EmptyState icon="📅" title="No items with expiry dates set" sub="Add expiry dates when editing inventory items or creating batch records." />
      ) : filtered.length === 0 ? (
        <EmptyState icon="✅" title="No items match this filter" sub="Try selecting a different time window." />
      ) : (
        <div className="ii-table-wrap">
          <table className="ii-table">
            <thead>
              <tr>
                {['Source','Product','Batch No.','Qty','Expiry Date','Days Left','Status','Action'].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const dl = daysLabel(r.daysUntilExpiry);
                const sm = EXPIRY_STATUS_MAP[r.status] || EXPIRY_STATUS_MAP.good;
                return (
                  <tr key={i}>
                    <td>
                      <span className={`ii-badge ${r.source === 'batch' ? 'ii-source-batch' : 'ii-source-item'}`}>
                        {r.source === 'batch' ? 'Batch' : 'Item'}
                      </span>
                    </td>
                    <td><span className="ii-cell-name">{r.name}</span></td>
                    <td><span className="ii-mono" style={{ fontSize: '0.78rem', color: C.muted }}>{r.batchNumber || '—'}</span></td>
                    <td style={{ fontWeight: 600, color: C.text }}>{r.quantity}</td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                      {new Date(r.expiryDate).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td style={{ fontWeight: 700, color: dl.color }}>{dl.text}</td>
                    <td>
                      <span className="ii-badge" style={{ background: sm.bg, color: sm.color, borderColor: sm.border }}>{sm.label}</span>
                    </td>
                    <td>
                      {r.status === 'expired' && (
                        <button className="ii-btn-danger">Remove</button>
                      )}
                      {(r.status === 'critical' || r.status === 'warning') && (
                        <button className="ii-btn-warn">Discount</button>
                      )}
                      {(r.status === 'watch' || r.status === 'good') && (
                        <button className="ii-btn-ghost">View</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tab 2: Auto-Reorder ────────────────────────────────────────────────────────

function ReorderTab() {
  const [data, setData]       = useState({ items: [], totalCount: 0, totalEstimatedCost: 0 });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [toast, setToast]     = useState('');
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/inventory-alerts/reorder`, { headers: hdrs() });
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doOrder = async (id) => {
    setSending((s) => ({ ...s, [id]: true }));
    try {
      const r = await fetch(`${API}/inventory-alerts/reorder/${id}`, { method: 'POST', headers: hdrs() });
      const j = await r.json();
      setToast(j.message || 'Order recorded. Email sent to admin.');
      setConfirm(null);
      setTimeout(() => setToast(''), 4000);
    } finally { setSending((s) => ({ ...s, [id]: false })); }
  };

  const alerts = data.items || [];

  return (
    <div>
      <ToastMsg msg={toast} />
      {alerts.length > 0 && (
        <div className="ii-alert danger" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          <span>🚨 {alerts.length} item{alerts.length > 1 ? 's need' : ' needs'} restocking — Estimated cost: {gh(data.totalEstimatedCost)}</span>
          <button className="ii-btn-order-all" onClick={() => {
            if (window.confirm(`Order all ${alerts.length} items? Total estimated cost: ${gh(data.totalEstimatedCost)}`)) {
              alerts.forEach((a) => doOrder(a.id));
            }
          }}>Order All</button>
        </div>
      )}

      {loading ? (
        <div className="ii-loading">Loading…</div>
      ) : alerts.length === 0 ? (
        <EmptyState icon="✅" title="All items are well stocked" sub="Items appear here when quantity drops below the Low Stock Alert threshold on each item." />
      ) : (
        <div className="ii-table-wrap">
          <table className="ii-table">
            <thead>
              <tr>
                {['Product','Category','Current Stock','Reorder At','Suggested Qty','Supplier','Est. Cost','Action'].map((h) => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => {
                const pct = Math.min((a.quantity / Math.max(a.reorderLevel, 1)) * 100, 100);
                return (
                  <tr key={a.id}>
                    <td><span className="ii-cell-name">{a.name}</span></td>
                    <td>{a.category || '—'}</td>
                    <td style={{ minWidth: 130 }}>
                      <div className="ii-stock-bar-wrap">
                        <div className="ii-stock-bar-track">
                          <div className="ii-stock-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="ii-mono" style={{ color: C.coral, fontSize: '0.82rem', minWidth: 24 }}>{a.quantity}</span>
                      </div>
                    </td>
                    <td style={{ color: C.muted }}>{a.reorderLevel}</td>
                    <td style={{ fontWeight: 600, color: C.text }}>{a.reorderQuantity}</td>
                    <td>
                      {a.supplierName || '—'}
                      {a.leadTimeDays && <span style={{ color: C.muted, fontSize: '0.68rem', marginLeft: 5 }}>({a.leadTimeDays}d)</span>}
                    </td>
                    <td><span className="ii-mono">{gh(a.estimatedCost)}</span></td>
                    <td>
                      <button
                        className="ii-btn-order"
                        onClick={() => setConfirm({ id: a.id, name: a.name, qty: a.reorderQuantity, supplier: a.supplierName })}
                        disabled={sending[a.id]}
                      >
                        {sending[a.id] ? '…' : 'Order'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirm && (
        <Modal title="Confirm Order" onClose={() => setConfirm(null)} maxWidth={400}>
          <p className="ii-confirm-text">
            Order <strong style={{ color: C.text }}>{confirm.qty} units</strong> of <strong style={{ color: C.text }}>{confirm.name}</strong>
            {confirm.supplier ? ` from ${confirm.supplier}` : ''}?
          </p>
          <div className="ii-modal-foot">
            <button className="ii-btn-outline" onClick={() => setConfirm(null)}>Cancel</button>
            <button className="ii-btn-primary" onClick={() => doOrder(confirm.id)} disabled={sending[confirm.id]}>
              {sending[confirm.id] ? 'Sending…' : 'Confirm Order'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tab 3: Suppliers ───────────────────────────────────────────────────────────

const SUP0 = { name: '', category: '', phone: '', email: '', address: '', leadTimeDays: 3, rating: 5, notes: '' };

function SuppliersTab() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState(SUP0);
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/suppliers`, { headers: hdrs() });
      if (r.ok) setSuppliers(await r.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sf = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const isEdit = modal && modal !== 'add';
      const r = await fetch(isEdit ? `${API}/suppliers/${modal.id}` : `${API}/suppliers`, {
        method: isEdit ? 'PUT' : 'POST', headers: hdrs(), body: JSON.stringify(form),
      });
      if (r.ok) { await load(); setModal(null); }
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    setDeleting((d) => ({ ...d, [id]: true }));
    try {
      await fetch(`${API}/suppliers/${id}`, { method: 'DELETE', headers: hdrs() });
      await load();
    } finally { setDeleting((d) => ({ ...d, [id]: false })); }
  };

  const totalSpend = suppliers.reduce((s, x) => s + Number(x.totalSpend || 0), 0);

  return (
    <div>
      <div className="ii-row-between">
        <div className="ii-row-meta">
          {suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''} · Total spend: {gh(totalSpend)}
        </div>
        <button className="ii-btn-primary" onClick={() => { setForm(SUP0); setModal('add'); }}>+ Add Supplier</button>
      </div>

      {loading ? (
        <div className="ii-loading">Loading…</div>
      ) : suppliers.length === 0 ? (
        <EmptyState icon="🏭" title="No suppliers added yet" sub="Add suppliers to link them to inventory items and track your spending per supplier." />
      ) : (
        <div className="ii-table-wrap">
          <table className="ii-table">
            <thead>
              <tr>
                {['Supplier','Category','Contact','Lead Time','Total Spend','Rating','Actions'].map((h) => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s) => {
                const initials = s.name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
                return (
                  <tr key={s.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="ii-sup-avatar">{initials}</div>
                        <span className="ii-cell-name">{s.name}</span>
                      </div>
                    </td>
                    <td>{s.category || '—'}</td>
                    <td>
                      {s.phone && <div style={{ fontSize: '0.82rem', color: C.sub }}>{s.phone}</div>}
                      {s.email && <div style={{ fontSize: '0.75rem', color: C.muted }}>{s.email}</div>}
                      {!s.phone && !s.email && <span style={{ color: C.border }}>—</span>}
                    </td>
                    <td>{s.leadTimeDays}d</td>
                    <td><span className="ii-mono">{gh(s.totalSpend)}</span></td>
                    <td style={{ letterSpacing: 1 }}>
                      {'★'.repeat(s.rating)}<span className="ii-star-empty">{'★'.repeat(5 - s.rating)}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 7 }}>
                        <button className="ii-btn-edit" onClick={() => { setForm({ ...s }); setModal(s); }}>Edit</button>
                        <button className="ii-btn-danger" onClick={() => del(s.id)} disabled={deleting[s.id]}>
                          {deleting[s.id] ? '…' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title={modal === 'add' ? 'Add Supplier' : 'Edit Supplier'} onClose={() => setModal(null)}>
          <Field label="Supplier Name *">
            <input className="ii-input" value={form.name} onChange={sf('name')} placeholder="e.g. Accra Mills" />
          </Field>
          <div className="ii-2col">
            <Field label="Category">
              <input className="ii-input" value={form.category || ''} onChange={sf('category')} placeholder="e.g. Groceries" />
            </Field>
            <Field label="Lead Time (days)">
              <input className="ii-input" type="number" min="1" value={form.leadTimeDays} onChange={sf('leadTimeDays')} />
            </Field>
          </div>
          <div className="ii-2col">
            <Field label="Phone">
              <input className="ii-input" value={form.phone || ''} onChange={sf('phone')} placeholder="+233 XX XXX XXXX" />
            </Field>
            <Field label="Email">
              <input className="ii-input" type="email" value={form.email || ''} onChange={sf('email')} placeholder="contact@supplier.com" />
            </Field>
          </div>
          <Field label="Address">
            <input className="ii-input" value={form.address || ''} onChange={sf('address')} placeholder="Supplier address" />
          </Field>
          <Field label="Rating (1–5)">
            <div className="ii-rating-row">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} className="ii-star-btn" onClick={() => setForm((f) => ({ ...f, rating: n }))}>
                  <span style={{ color: n <= form.rating ? C.gold : 'rgba(255,255,255,0.15)' }}>★</span>
                </button>
              ))}
            </div>
          </Field>
          <Field label="Notes">
            <textarea className="ii-input" style={{ minHeight: 72, resize: 'vertical' }} value={form.notes || ''} onChange={sf('notes')} />
          </Field>
          <div className="ii-modal-foot">
            <button className="ii-btn-outline" onClick={() => setModal(null)}>Cancel</button>
            <button className="ii-btn-primary" onClick={save} disabled={saving || !form.name?.trim()}>
              {saving ? 'Saving…' : modal === 'add' ? 'Add Supplier' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tab 4: Batch Tracking ──────────────────────────────────────────────────��───

const BATCH0 = { batchNumber: '', autoGenerate: true, itemId: '', supplierId: '',
  quantityReceived: '', costPerUnit: '', expiryDate: '', receivedDate: '', notes: '' };

function batchStatus(b) {
  if (!b.expiryDate) {
    const pct = b.quantityRemaining / Math.max(b.quantityReceived, 1);
    if (pct === 0) return { label: 'Depleted', bg: 'rgba(136,146,164,0.1)',  color: C.muted, border: 'rgba(136,146,164,0.2)' };
    if (pct < 0.2) return { label: 'Low',      bg: 'rgba(240,180,41,0.1)',  color: C.gold,  border: 'rgba(240,180,41,0.25)' };
    return           { label: 'Active',    bg: 'rgba(56,189,248,0.1)',   color: C.sky,   border: 'rgba(56,189,248,0.25)' };
  }
  const days = Math.ceil((new Date(b.expiryDate) - Date.now()) / 86_400_000);
  if (days < 0)  return { label: 'Expired',  bg: 'rgba(255,107,107,0.12)', color: C.coral, border: 'rgba(255,107,107,0.3)' };
  if (days < 30) return { label: 'Expiring', bg: 'rgba(255,107,107,0.1)',  color: C.coral, border: 'rgba(255,107,107,0.25)' };
  const pct = b.quantityRemaining / Math.max(b.quantityReceived, 1);
  if (pct < 0.2) return { label: 'Low',      bg: 'rgba(240,180,41,0.1)',  color: C.gold,  border: 'rgba(240,180,41,0.25)' };
  return           { label: 'Active',    bg: 'rgba(56,189,248,0.1)',   color: C.sky,   border: 'rgba(56,189,248,0.25)' };
}

function BatchTab() {
  const [batches, setBatches]     = useState([]);
  const [items, setItems]         = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(false);
  const [form, setForm]           = useState(BATCH0);
  const [saving, setSaving]       = useState(false);
  const [filterItem, setFilterItem] = useState('');
  const [toast, setToast]           = useState('');
  const [deleting, setDeleting]     = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [br, ir, sr] = await Promise.all([
        fetch(`${API}/batches`,   { headers: hdrs() }),
        fetch(`${API}/inventory`, { headers: hdrs() }),
        fetch(`${API}/suppliers`, { headers: hdrs() }),
      ]);
      if (br.ok) setBatches(await br.json());
      if (ir.ok) setItems(await ir.json());
      if (sr.ok) setSuppliers(await sr.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const bf = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.itemId || !form.quantityReceived || !form.costPerUnit) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/batches`, {
        method: 'POST',
        headers: hdrs(),
        body: JSON.stringify({
          batchNumber:      form.autoGenerate ? '' : form.batchNumber,
          itemId:           form.itemId,
          supplierId:       form.supplierId || null,
          quantityReceived: form.quantityReceived,
          costPerUnit:      form.costPerUnit,
          expiryDate:       form.expiryDate || null,
          receivedDate:     form.receivedDate || null,
          notes:            form.notes || null,
        }),
      });
      if (r.ok) {
        const itemName = items.find((i) => String(i.id) === String(form.itemId))?.name || '';
        setToast(`Batch created. Inventory updated +${form.quantityReceived}${itemName ? ` (${itemName})` : ''}`);
        setTimeout(() => setToast(''), 4000);
        await load();
        setModal(false);
        setForm(BATCH0);
      }
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this batch?')) return;
    setDeleting((d) => ({ ...d, [id]: true }));
    try {
      await fetch(`${API}/batches/${id}`, { method: 'DELETE', headers: hdrs() });
      await load();
    } finally { setDeleting((d) => ({ ...d, [id]: false })); }
  };

  const filtered   = filterItem ? batches.filter((b) => String(b.itemId) === filterItem) : batches;
  const activeCnt  = batches.filter((b) => batchStatus(b).label === 'Active').length;
  const expiringCnt = batches.filter((b) => batchStatus(b).label === 'Expiring').length;

  return (
    <div>
      <ToastMsg msg={toast} />
      <div className="ii-row-between">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="ii-row-meta">
            {activeCnt} active batch{activeCnt !== 1 ? 'es' : ''}
            {expiringCnt > 0 && <span style={{ color: C.coral, marginLeft: 8, fontWeight: 600 }}>· {expiringCnt} expiring soon</span>}
          </div>
          <select className="ii-filter-select" value={filterItem} onChange={(e) => setFilterItem(e.target.value)}>
            <option value="">All Products</option>
            {items.map((i) => <option key={i.id} value={String(i.id)}>{i.name}</option>)}
          </select>
        </div>
        <button className="ii-btn-primary" onClick={() => { setForm(BATCH0); setModal(true); }}>+ Add Batch</button>
      </div>

      {loading ? (
        <div className="ii-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon="📦" title="No batches recorded yet" sub="Batch tracking helps pharmacies, food vendors and importers manage stock by lot number." />
      ) : (
        <div className="ii-table-wrap">
          <table className="ii-table">
            <thead>
              <tr>
                {['Batch No.','Product','Supplier','Qty Received','Qty Remaining','Cost/Unit','Received','Expiry','Status','Actions'].map((h) => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => {
                const st = batchStatus(b);
                const remaining = b.quantityRemaining;
                const remainColor = remaining === 0 ? C.coral : remaining < b.quantityReceived * 0.2 ? C.gold : C.text;
                return (
                  <tr key={b.id}>
                    <td><span className="ii-mono" style={{ color: C.sky, fontSize: '0.82rem' }}>{b.batchNumber}</span></td>
                    <td><span className="ii-cell-name">{b.item?.name || '—'}</span></td>
                    <td>{b.supplier?.name || '—'}</td>
                    <td style={{ color: C.sub }}>{b.quantityReceived}</td>
                    <td style={{ fontWeight: 700, color: remainColor }}>{remaining}</td>
                    <td><span className="ii-mono">{gh(b.costPerUnit)}</span></td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                      {new Date(b.receivedDate || b.createdAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                      {b.expiryDate
                        ? new Date(b.expiryDate).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' })
                        : <span style={{ color: C.border }}>—</span>}
                    </td>
                    <td>
                      <span className="ii-badge" style={{ background: st.bg, color: st.color, borderColor: st.border }}>{st.label}</span>
                    </td>
                    <td>
                      <button className="ii-btn-danger" onClick={() => del(b.id)} disabled={deleting[b.id]}>
                        {deleting[b.id] ? '…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title="Add Batch" onClose={() => setModal(false)}>
          <Field label="Batch Number">
            <label className="ii-checkbox-row" style={{ marginBottom: 8 }}>
              <input type="checkbox" checked={form.autoGenerate}
                onChange={(e) => setForm((f) => ({ ...f, autoGenerate: e.target.checked }))} />
              Auto-generate (B-{new Date().getFullYear()}-NNN)
            </label>
            {!form.autoGenerate && (
              <input className="ii-input" value={form.batchNumber} onChange={bf('batchNumber')} placeholder="e.g. B-2026-001" />
            )}
          </Field>
          <Field label="Product *">
            <select className="ii-input" value={form.itemId} onChange={bf('itemId')}>
              <option value="">Select product…</option>
              {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </Field>
          <Field label="Supplier">
            <select className="ii-input" value={form.supplierId} onChange={bf('supplierId')}>
              <option value="">None</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <div className="ii-2col">
            <Field label="Qty Received *">
              <input className="ii-input" type="number" min="1" value={form.quantityReceived} onChange={bf('quantityReceived')} />
            </Field>
            <Field label="Cost per Unit (GHS) *">
              <input className="ii-input" type="number" step="0.01" min="0" value={form.costPerUnit} onChange={bf('costPerUnit')} />
            </Field>
          </div>
          <div className="ii-2col">
            <Field label="Expiry Date" hint="Required for food/pharma items">
              <input className="ii-input" type="date" value={form.expiryDate} onChange={bf('expiryDate')} />
            </Field>
            <Field label="Received Date" hint="Defaults to today">
              <input className="ii-input" type="date" value={form.receivedDate} onChange={bf('receivedDate')} />
            </Field>
          </div>
          <Field label="Notes">
            <textarea className="ii-input" style={{ minHeight: 64, resize: 'vertical' }} value={form.notes} onChange={bf('notes')} />
          </Field>
          <div className="ii-modal-foot">
            <button className="ii-btn-outline" onClick={() => setModal(false)}>Cancel</button>
            <button
              className="ii-btn-primary"
              onClick={save}
              disabled={saving || !form.itemId || !form.quantityReceived || !form.costPerUnit}
            >
              {saving ? 'Adding…' : 'Add Batch'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function InventoryIntelligence() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tabIdx = TAB_KEYS.indexOf(tabParam) >= 0 ? TAB_KEYS.indexOf(tabParam) : 0;
  const [tab, setTab] = useState(tabIdx);

  const switchTab = (i) => { setTab(i); setSearchParams({ tab: TAB_KEYS[i] }); };

  useEffect(() => {
    const idx = TAB_KEYS.indexOf(searchParams.get('tab'));
    if (idx >= 0 && idx !== tab) setTab(idx);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      <style>{STYLES}</style>
      <div className="ii-shell">
        <h1 className="ii-title">Inventory Intelligence</h1>
        <p className="ii-subtitle">Expiry tracking, reorder alerts, supplier management, and batch control</p>

        <div className="ii-tabs">
          {TAB_LABELS.map((t, i) => (
            <button key={t} className={`ii-tab${tab === i ? ' active' : ''}`} onClick={() => switchTab(i)}>{t}</button>
          ))}
        </div>

        <div className="ii-content">
          {tab === 0 && <ExpiryTab />}
          {tab === 1 && <ReorderTab />}
          {tab === 2 && <SuppliersTab />}
          {tab === 3 && <BatchTab />}
        </div>
      </div>
    </>
  );
}
